/**
 * The signed-in control in every learner-app header — the public landing
 * page, the learner masthead and the app shell all mount this one component,
 * so "who am I and where can I go" reads the same on each.
 *
 * It replaces two earlier presentations that disagreed with each other: the
 * bare initials circle on the learner masthead (`AvatarMenu`) and the inline
 * "Name — Institution (institution_admin) switch Sign out" row the app shell
 * rendered from `SessionControls`. What the design puts in their place is the
 * school-admin console's user card, brought over to the light surface: the school
 * logo (or initials), the name, and under it the acting workspace with the role
 * spelled in words. It is a BUTTON with a chevron, and what it opens, in
 * order:
 *
 *   1. who is signed in — the avatar again, the name, the email;
 *   2. the learner nav, on the widths where the bar cannot draw it (see
 *      `LearnerNavContext` — this is still the menu that carries it);
 *   3. the way into the OTHER surface the acting role belongs to — "Open
 *      school admin" for an administrator reading the learner app, because
 *      the same person is both and the hop should be one click, not a sign-out;
 *   4. the workspace list — every membership the session holds, the acting
 *      one checked, each a row that switches IN PLACE (the login screen's
 *      picker, one click down instead of a round trip);
 *   5. whatever the header hands over (`tools` — the learner masthead's theme
 *      toggle, which has nowhere else to live there), account settings, and
 *      sign out.
 *
 * WHERE A ROW LEADS is the login screen's rule, reused rather than restated:
 * `destinationForRole` says which surface a role belongs to. A learner
 * membership becomes the acting identity and the router goes to the course
 * list, because whatever course was under the reader may not exist in the
 * next institution. An admin or faculty membership is a different app on the
 * same origin, so that row sets the identity and LEAVES for it — identity is
 * in localStorage, which both apps share.
 *
 * The session logic is REUSED from `useSessionControls`; this component owns
 * the card, the popover and the accessibility around them. The school-admin
 * console has its own copy of this arrangement (`apps/admin/.../AccountMenu`)
 * on the dark rail; the two are kept in step by hand, and each carries the one
 * thing the other surface has no use for (the nav here, the rail card there).
 *
 * TWO MODES, ONE CARD. With a live session the card names the session user and
 * the menu lists their memberships. Outside production, with no session, the
 * header fallback is still authenticating: the card names the acting dev
 * identity and the workspace slot hosts the dev switcher instead. A cold start
 * with NO identity at all has nothing to put on a card, so the dev switcher
 * renders inline — it must be MOUNTED to auto-select its preferred identity,
 * and a switcher inside a closed menu never would be.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { TenantSummary } from '@cusp/contracts';
import {
  destinationForRole,
  leaveForApp,
  membershipIdentity,
  setIdentity,
  useIdentity,
  type Identity,
  type Surface,
} from '@cusp/api-client';
import { signOut, useSessionControls } from '@cusp/api-client/auth-ui';
import { onFillFor } from '@cusp/theme';
import { ROLE_LABELS } from '../roleLabels.js';
import { useLearnerNav } from '../screens/learner/chrome.js';
import { InstitutionMark } from './InstitutionMark.js';
import { initialsFrom, markFrom } from '../screens/learner/name.js';

export interface AccountMenuProps {
  /**
   * Where the login screen lives; sign-out lands there. An ABSOLUTE path on
   * purpose: the deployed proxy routes one hostname by path, and a router
   * `Link` could never express the hop the admin app makes to the same screen.
   */
  loginPath?: string;
  /**
   * Rendered as the workspace affordance when there is no live session — the
   * dev identity switcher, outside production. Injected (not hard-wired) so
   * this component stays testable without the dev-identities network call.
   */
  devFallback?: ReactNode;
  /**
   * A control the header wants inside the menu rather than beside the card —
   * the learner masthead's theme toggle. Rendered as a row of its own above
   * the account actions; the landing page and the app shell keep theirs on the
   * bar and pass nothing.
   */
  tools?: ReactNode;
}

/**
 * The button into the OTHER surface, named for where it goes. Keyed on the
 * surface rather than the role so faculty and administrators — both served by
 * the school-admin console — get one label, and so a learner (whose surface
 * is this one) gets none.
 */
const OPEN_LABELS: Partial<Record<Surface, string>> = {
  admin: 'Open school admin',
  console: 'Open platform console',
};

export function AccountMenu({ loginPath = '/login', devFallback, tools }: AccountMenuProps) {
  const identity = useIdentity();
  const nav = useLearnerNav();
  const navigate = useNavigate();
  /**
   * DOES THIS MENU CARRY THE LEARNER NAV? One expression, read in two places
   * that must never disagree: the attribute that lets the bar give its nav up,
   * and whether the items are rendered here at all. Gating only the attribute
   * once left the bar keeping its nav — correctly — while the menu drew a
   * second, inert copy beside it. (Codex review, 2026-08-27.)
   */
  const carriesNav = nav?.onNavigate !== undefined;
  const state = useSessionControls();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // The acting identity (a membership triple) is the truth in both modes —
  // the dev switcher and a real session both write it — so it names the card.
  // A signed-in caller who has not selected a membership yet still has a
  // session user to show.
  const sessionUser = state.status === 'authed' ? state.session.user : undefined;
  const name = identity?.userName ?? sessionUser?.name;
  const email = identity?.email ?? sessionUser?.email;
  const tenantName =
    identity?.tenantName ?? (state.status === 'authed' ? state.acting?.name : undefined);
  const role = identity?.role ?? (state.status === 'authed' ? state.acting?.role : undefined);

  const close = useCallback((focusButton: boolean) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  // Close on outside pointer and on Escape. Bound only while open so a page
  // full of these does not install idle listeners.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // On open, land keyboard users on the first item, where the pointer would.
  useEffect(() => {
    if (!open) return;
    focusables(menuRef.current)[0]?.focus();
  }, [open]);

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    // NOT A MODAL. A menu button's menu closes on Tab and lets focus carry on
    // from the button, per the ARIA menu-button pattern — trapping Tab here
    // would leave a keyboard user with no way past the card except knowing to
    // press Escape. Focus goes to the button BEFORE the default action runs,
    // so the same keypress then moves on from the card, forwards or back.
    if (e.key === 'Tab') {
      close(true);
      return;
    }
    // The dev switcher is a native <select>: arrow keys are how it is
    // operated, and stealing them would leave the identity un-changeable
    // from the keyboard. Roaming is for the menu's own items only.
    const target = e.target as HTMLElement;
    if (target.tagName === 'SELECT' || target.tagName === 'INPUT') return;
    const items = focusables(menuRef.current);
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (index: number) => {
      e.preventDefault();
      items[(index + items.length) % items.length]?.focus();
    };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
  };

  /** Act as this membership, and go where that role belongs. */
  const choose = (membership: TenantSummary) => {
    if (state.status !== 'authed') return;
    if (isActing(identity, membership)) {
      close(true);
      return;
    }
    const dest = destinationForRole(membership.role);
    // Nowhere built to go: leave the acting identity alone rather than
    // switching the reader into a surface that does not exist.
    if (!dest.built) return;
    setIdentity(membershipIdentity(state.session, membership));
    // Focus back on the card BEFORE the row it was on is hidden: a keyboard
    // user who pressed Enter here would otherwise be left on nothing.
    close(true);
    if (dest.surface === 'learner') {
      // A literal, not `dest.href`: TanStack's typed router only accepts known
      // route paths, and `routing.test.ts` pins the table's learner href to
      // this value so the two cannot drift. The course list rather than the
      // current URL, because every course screen is scoped to an entitlement
      // the next institution may not hold.
      void navigate({ to: '/courses' });
    } else {
      leaveForApp(dest.href);
    }
  };

  // No identity and no session user: nothing to put on a card. Outside
  // production the dev switcher stands in (see the header comment for why it
  // has to be mounted); in production this header shows nothing, and the
  // landing page draws its "Sign in" link instead.
  if (!name) return state.status === 'anon' ? <>{devFallback ?? null}</> : null;

  const initials = initialsFrom(name);
  // The other surface the acting role belongs to, if there is one to open.
  const acting = role ? destinationForRole(role) : undefined;
  const openLabel = acting && acting.built ? OPEN_LABELS[acting.surface] : undefined;
  const workspaces = state.status === 'authed' && state.canSwitch;
  const headingId = `${menuId}-ws`;
  const subline = tenantName
    ? role
      ? `${tenantName} · ${ROLE_LABELS[role]}`
      : tenantName
    : (email ?? 'No workspace selected');

  return (
    <div className="account" ref={rootRef}>
      <button
        type="button"
        ref={buttonRef}
        className="account-btn"
        /* THE BAR ONLY GIVES ITS NAV UP TO A MENU THAT WILL CARRY IT, and this
           attribute is the whole of that agreement — `.l-mast-nav`'s hiding
           rule below 860px is scoped to a masthead containing one. It lives on
           the BUTTON, which renders whenever this component does, and it asks
           whether this menu can actually NAVIGATE — a masthead that provides
           items but no handler gets to keep its bar nav. */
        data-learner-nav={carriesNav ? '' : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Account menu — ${name}`}
        onClick={() => setOpen((v) => !v)}
      >
        <InstitutionMark identity={identity} fallback={initials} className="account-av" />
        <span className="account-who">
          <span className="account-name">{name}</span>
          <span className="account-sub">{subline}</span>
        </span>
        <Icon className="account-chev" size={16} stroke={2.5}>
          <path d="m6 9 6 6 6-6" />
        </Icon>
      </button>

      {/* Always in the tree, `hidden` when closed, rather than conditionally
          rendered: the dev switcher inside it has to stay MOUNTED to keep the
          acting identity honest (it drops one its role list does not offer),
          and a switcher that only exists while the menu is open would leave a
          stale pick on the card until somebody clicked. */}
      <div
        id={menuId}
        ref={menuRef}
        className="account-menu"
        role="menu"
        aria-label="Account"
        tabIndex={-1}
        hidden={!open}
        onKeyDown={onMenuKeyDown}
      >
        {/* 1 — who. The context line the old menu opened with, as the design
            draws it: avatar, name, email. Not an item — nothing to do here. */}
        <div className="account-section account-user" role="presentation">
          <span className="account-user-av" aria-hidden="true">
            {initials}
          </span>
          <span className="account-user-who">
            <span className="account-user-name">{name}</span>
            {email !== undefined && <span className="account-user-email">{email}</span>}
          </span>
        </div>

        {/* 2 — THE NAV, ON THE WIDTHS WHERE THE BAR CANNOT SHOW IT. Below
            860px `.l-mast-nav` is not drawn, and this is what keeps "the same
            navigation on every screen" from being a desktop-only claim. Hidden
            by CSS rather than a JS breakpoint, so there is one source of truth
            for where the bar stops fitting — and `display: none` above it
            keeps a screen reader from being read two copies of the same nav.

            The unbuilt items keep their "soon" chip here for the same reason
            they carry it on the bar: silence after a click reads as broken,
            and a tooltip is no use on the devices this breakpoint serves. */}
        {carriesNav && nav !== null && (
          <div className="account-section account-nav" role="none">
            {nav.items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className="account-item"
                aria-current={nav.current === item.key ? 'page' : undefined}
                aria-disabled={item.built ? undefined : 'true'}
                disabled={!item.built}
                onClick={
                  item.built
                    ? () => {
                        close(false);
                        nav.onNavigate?.(item.key);
                      }
                    : undefined
                }
              >
                {item.label}
                {!item.built && <span className="account-soon">soon</span>}
              </button>
            ))}
          </div>
        )}

        {/* 3 + 4 — where else this person can go. */}
        {(openLabel !== undefined || workspaces || state.status === 'anon') && (
          <div
            className="account-section"
            role={workspaces ? 'group' : 'none'}
            aria-labelledby={workspaces ? headingId : undefined}
          >
            {openLabel !== undefined && acting !== undefined && (
              <button
                type="button"
                role="menuitem"
                className="btn btn-primary btn-block account-open"
                onClick={() => {
                  close(false);
                  leaveForApp(acting.href);
                }}
              >
                {openLabel}
              </button>
            )}

            {/* A real session with more than one membership lists them, the
                acting one checked. Outside production the dev switcher takes
                the slot.

                ONE ROW PER INSTITUTION, keyed on the tenant alone, because a
                person holds exactly one role at an institution
                (`memberships_tenant_user_uq`): the API resolves the acting
                role from (tenant, user), so a second row at the same school
                with another role would be one the platform could never
                honour. */}
            {state.status === 'authed' ? (
              workspaces && (
                <>
                  <p className="account-head" id={headingId} role="presentation">
                    Switch workspace
                  </p>
                  {state.session.memberships.map((m) => {
                    const here = isActing(identity, m);
                    return (
                      <button
                        key={m.tenantId}
                        type="button"
                        role="menuitemradio"
                        aria-checked={here}
                        className="account-ws"
                        onClick={() => choose(m)}
                      >
                        <InstitutionMark
                          identity={open ? membershipIdentity(state.session, m) : null}
                          fallback={markFrom(m.name)}
                          className={`account-ws-mark${here ? ' account-ws-mark-here' : ''}`}
                          style={swatchStyle(m.brandPrimary)}
                        />
                        <span className="account-ws-who">
                          <span className="account-ws-name">{m.name}</span>
                          <span className="account-ws-role">{ROLE_LABELS[m.role]}</span>
                        </span>
                        {here && (
                          <Icon className="account-ws-check" size={18} stroke={2.5}>
                            <path d="M20 6 9 17l-5-5" />
                          </Icon>
                        )}
                      </button>
                    );
                  })}
                </>
              )
            ) : state.status === 'anon' && devFallback !== undefined ? (
              <div className="account-control" role="none">
                {devFallback}
              </div>
            ) : null}
          </div>
        )}

        {/* 5 — the header's own control, then the account actions. */}
        <div className="account-section" role="none">
          {tools !== undefined && (
            <div className="account-control" role="none">
              {tools}
            </div>
          )}

          {/* Account settings: not built, and honest about it — a real
              disabled control carrying its reason, not a row that clicks to
              nothing. */}
          <button
            type="button"
            role="menuitem"
            className="account-item"
            disabled
            aria-disabled="true"
            title="Available at rollout"
          >
            <Icon className="account-item-icon" size={18} stroke={2}>
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </Icon>
            Account settings
            <span className="account-soon">soon</span>
          </button>

          {/* Sign out — only a real session can. */}
          {state.status === 'authed' && (
            <button
              type="button"
              role="menuitem"
              className="account-item"
              onClick={() => {
                close(false);
                signOut(loginPath);
              }}
            >
              <Icon className="account-item-icon" size={18} stroke={2}>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="m16 17 5-5-5-5M21 12H9" />
              </Icon>
              Sign out
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A workspace row's swatch, in the OTHER school's colour.
 *
 * TWO PROPERTIES, NOT ONE, AND THE SECOND IS THE POINT. A row in this list is
 * some other institution's brand, so its mark cannot borrow the acting tenant's
 * `--color-on-accent`: that value was derived for the colour the DOCUMENT is
 * painted in, and a navy chip carrying near-black text because the acting
 * school happens to be orange is unreadable. `onFillFor` derives the foreground
 * for this row's own fill, with the guarantee `deriveBrand` gives — at least
 * 4.5:1, always.
 *
 * `undefined` for an unbranded school, deliberately: the CSS falls back to what
 * it drew before this existed, so null stays a first-class state rather than a
 * blank square.
 *
 * The property NAMES are the contract with `.account-ws-mark` in `index.css`.
 * The console's account menu carries the same pair (the two menus are kept in
 * step by hand — see this file's header).
 */
function swatchStyle(primary: string | null): CSSProperties | undefined {
  if (primary === null) return undefined;
  return { '--ws-fill': primary, '--ws-on-fill': onFillFor(primary) } as CSSProperties;
}

/**
 * Is this membership the one being acted as? Matched on the tenant alone —
 * the same test `adoptSessionIdentity` and `useSessionControls` apply — since
 * one person has one membership per tenant.
 */
function isActing(identity: Identity | null, m: Pick<TenantSummary, 'tenantId'>): boolean {
  return identity !== null && identity.tenantId === m.tenantId;
}

/** Decorative stroke icon — every one sits beside its own word. */
function Icon(props: { className: string; size: number; stroke: number; children: ReactNode }) {
  return (
    <svg
      className={props.className}
      width={props.size}
      height={props.size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={props.stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {props.children}
    </svg>
  );
}

/** Visible, focusable descendants of the menu, in DOM order. */
function focusables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), select, input'),
  );
}
